import { createContext, forwardRef, startTransition, Suspense, useContext, useLayoutEffect, useState } from "react"
import { useMotionValue } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { cssVarHeight } from "contexts/Header"

import getMediaQuery from "css/breakpoints"
import * as button from "css/buttons"
import { fullGrid } from "css/grid"

import useSSR from "hooks/useSSR"

import Ballerinas from "components/family-variations/Ballerinas"
import Icon from "components/family-variations/Icon"
import Infos from "components/family-variations/Infos"
import WishlistButton from "components/wishlist-button/WishlistButton"

const Context = createContext()
export function useVariations() {
  return useContext(Context)
}

const Root = styled.div`
  ${fullGrid}
  grid-template-rows: 1fr [ballerina] 100vw 3rem [infos] auto 1fr;
  padding-block-start: 60px;

  ${({ heading }) =>
    heading
      ? `  
        height: 100vh;
        height: 100svh;`
      : `
        height: auto;
        ${getMediaQuery("m")} {
          height: 80vh;
          height: 80svh;
          min-height:42vw;
        }
  `}

  ${getMediaQuery("m")} {
    padding-block-start: 0;
    grid-template-rows: 1fr [ballerina infos] min(420px, calc(var(--grid-col-unit) * 8 + var(--grid-gap) * 7), 620px) 1fr;
  }

  ${getMediaQuery("l")} {
    grid-template-rows: 1fr [ballerina infos] clamp(420px, calc(var(--grid-col-unit) * 6 + var(--grid-gap) * 5), 620px) 1fr;
    padding-block-end: 40px;
  }

  position: relative;

  touch-action: pan-y;
  user-select: none;
  overflow: hidden;

  & > button {
    display: flex;
    align-items: center;
    margin-block-start: 0.5rem;
    margin-inline-start: -1.5rem;
    padding: calc(1rem + var(--grid-gap));
    width: fit-content;
    height: fit-content;
    grid-column: col 1;
    grid-row: ballerina;
    white-space: nowrap;

    ${getMediaQuery("m")} {
      grid-column: col 3 / span 3;
      grid-row: ballerina;
      padding: 1rem;
    }

    ${getMediaQuery("l")} {
      display: none;
    }
  }
`

function Variations({ variations, start, frames, theme, className, heading }, ref) {
  const console = useConsole()

  const ssr = useSSR()

  const [variation, setVariation] = useState(variations[0])

  const isFocused = useMotionValue(false)

  const ctx = {
    start,
    frames,
    variations,
    isFocused,
    get variation() {
      return variation?.rmc
    },
    setVariation(to) {
      const item = variations.find(({ rmc }) => rmc === to)
      if (item) startTransition(() => setVariation(item))
    },
  }

  console.verbose("FamilyVariations:Variations(%o)", { variations, variation, ctx })
  return (
    <Root ref={ref} className={className} heading={heading}>
      <Context.Provider value={ctx}>
        <Infos /*theme={theme}*/ className={className} />
        {!ssr && (
          <Suspense>
            <Ballerinas theme={theme} />
          </Suspense>
        )}
      </Context.Provider>
    </Root>
  )
}

export default forwardRef(Variations)
